import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { downloadBlob, PDF_MIME, XLSX_MIME, ZIP_MIME } from '../lib/download';
import { businessTodayIso } from '../lib/dates';
import { Button } from './Button';
import { useToast } from './Toast';

interface Props {
  document: 'quotations' | 'delivery-notes';
}

export function DocumentExportButtons({ document }: Props): JSX.Element | null {
  const { user, perms } = useAuth();
  const toast = useToast();
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | 'zip' | null>(null);
  const permission = document === 'quotations' ? 'quotations.view' : 'delivery_notes.create';
  if (!user || !['operations', 'admin', 'owner'].includes(user.role) || !perms.has(permission))
    return null;

  const exportAll = async (format: 'pdf' | 'xlsx' | 'zip'): Promise<void> => {
    if (exporting) return;
    setExporting(format);
    try {
      await downloadBlob(
        `/${document}/export.${format}`,
        {},
        format === 'zip' ? ZIP_MIME : format === 'pdf' ? PDF_MIME : XLSX_MIME,
        `${document}-all-${businessTodayIso()}.${format}`,
      );
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="secondary"
        title="Export all records, across every page and status"
        onClick={() => void exportAll('pdf')}
        loading={exporting === 'pdf'}
        disabled={exporting !== null}
      >
        Export all PDF
      </Button>
      <Button
        variant="secondary"
        title="Download all records as separate PDFs in a ZIP file"
        onClick={() => void exportAll('zip')}
        loading={exporting === 'zip'}
        disabled={exporting !== null}
      >
        Export PDFs ZIP
      </Button>
      <Button
        variant="secondary"
        title="Export all records, across every page and status"
        onClick={() => void exportAll('xlsx')}
        loading={exporting === 'xlsx'}
        disabled={exporting !== null}
      >
        Export all Excel
      </Button>
    </div>
  );
}
